import axios from "axios";
import AuthService, { LogoutReason } from "../services/authentication.service";
import Toast from "../components/Toast";
import { isPublicSurveyRoute } from "../../utils/constants";
import { handlePaymentRequired } from "../../components/Billing/paymentRequired";
import { rememberRefreshedSession } from "../../components/Billing/billingSignIn";
import { isSubscriptionEndedMessage } from "../../pages/auth/legacyBillingMessages";

const { REACT_APP_BASE_URL, REACT_APP_FILE_MANAGEMENT } = process.env;
let isRefreshing = false;
// The requests that met an expired token while the refresh was running, each
// told when it is over whether the session was renewed.
let refreshSubscribers = [];
// The refresh failed and the browser is on its way to the login page. Requests
// still out with the old token come back 401 after the sign out, and are not
// renewed again: a second sign out would send the login page no reason.
let isSessionEnded = false;

const https = axios.create({
   timeout: 60000,
   headers: { "Content-Type": "application/json" }
});

// True when the request set the header itself. Axios keeps the instance's
// defaults in their own buckets (`common`, `get`, ...) until after the
// interceptors have run, so a top-level key here is the request's own.
const hasOwnHeader = (headers, name) =>
   Object.keys(headers ?? {}).some((key) => key.toLowerCase() === name.toLowerCase());

export const setupInterceptor = (store, setGlobalLoader) => {
   https.interceptors.request.use((config) => {
      const token = AuthService.getAccessToken();
      const organizationId = AuthService.getOrganizationId();

      config.headers = config.headers ?? {};

      // Only what the browser actually holds is sent. With no token these went
      // out as `Authorization: Bearer null` and `X-OrganizationId: null`.
      //
      // A header the request set itself is kept: the billing client names the
      // organization it is acting on, and authorises a sign up's calls with the
      // token confirmEmail gave it (components/Billing/billingApi.js).
      if (!hasOwnHeader(config.headers, "Authorization")) {
         if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // The session's token, not the request's own: a retry after a
            // refresh swaps it for the new one (retryWithCurrentToken).
            config._sessionAuthorization = true;
         } else if (config.headers.common) {
            // A token the refresh below made a default outlives the sign in it
            // belonged to.
            delete config.headers.common.Authorization;
         }
      }

      if (organizationId && !hasOwnHeader(config.headers, "X-OrganizationId")) {
         config.headers["X-OrganizationId"] = organizationId;
      }

      if (config.url && config.url.startsWith("/FileData/getFile")) {
         config.baseURL = REACT_APP_FILE_MANAGEMENT;
      } else {
         config.baseURL = REACT_APP_BASE_URL;
      }

      return config;
   });

   https.interceptors.response.use(
      (response) => {
         const contentType = response.headers["content-type"];
         if (
            contentType &&
            contentType.includes(
               "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            )
         ) {
            return response;
         }

         if (!response.data.isSuccess) {
            const errMessages = response.data.errors?.length ? response.data.errors.join("") : response.data?.message;
            Toast.warning(errMessages);
         }
         return response;
      },
      (error) => {
         // The 402 gate: the organization is held at billing_only, and the
         // billing page takes over (components/Billing/paymentRequired.js).
         // Never a sign-in problem: the session is kept, since paying needs it.
         if (error?.response?.status === 402) {
            handlePaymentRequired(error.response.data);
         } else if(navigator.onLine) {
            if (isExpiredSession(error)) return renewSessionAndRetry(error);
         }
         else if (!navigator.onLine && !isPublicSurveyRoute()) {
            Toast.warning("Check your internet connection");
         }
         return Promise.reject(error);
      }
   );

   // A 401 the session can be renewed for: not a retry, and not the refresh
   // itself, which is sent as one.
   const isExpiredSession = (error) =>
      error.response?.status === 401 &&
      Boolean(error.config) &&
      !error.config._retry &&
      !(isSessionEnded && !AuthService.isAuthenticated());

   // The request again, once. The expired token the session gave it is
   // dropped, and the request interceptor sends the one held now.
   const retryWithCurrentToken = (config) => {
      if (config._sessionAuthorization) delete config.headers.Authorization;
      return https(config);
   };

   // The access token has expired: renew the session (POST
   // /Account/refreshToken) and send the request again, answering its caller
   // with that answer. A request that meets an expired token while the refresh
   // runs waits for it, then goes again too.
   //
   // Only a refresh that fails signs out. A retry is an ordinary request: a 402
   // on it is the gate's (above), and any other failure is its caller's.
   const renewSessionAndRetry = async (error) => {
      const originalRequest = error.config;
      originalRequest._retry = true;

      if (isRefreshing) {
         return new Promise((resolve, reject) => {
            refreshSubscribers.push((renewed) =>
               renewed ? resolve(retryWithCurrentToken(originalRequest)) : reject(error)
            );
         });
      }

      isRefreshing = true;
      setGlobalLoader(true);
      // The refresh token held is for this organization (selectOrganization
      // hands each its own), and so is everything the answer reports.
      const organizationId = AuthService.getOrganizationId();
      let refreshError = null;

      try {
         const { data } = await https.post(
            `/Account/refreshToken`,
            { token: AuthService.getRefreshToken() },
            { _retry: true }
         );
         const session = data?.data;
         if (!data?.isSuccess || !session?.token) {
            throw Object.assign(new Error("The session was not renewed"), { envelope: data });
         }

         // Unless another organization was picked meanwhile, whose session is
         // the one held now.
         if (AuthService.getOrganizationId() === organizationId) {
            AuthService.setTokens(session.token, session.refreshToken);
            https.defaults.headers.common["Authorization"] = `Bearer ${session.token}`;
            // Every refresh is a billing check: a grace period that ran out
            // while the user stayed signed in, or a payment that let the
            // organization back in (components/Billing/billingSignIn.js).
            rememberRefreshedSession(session, organizationId);
         }
      } catch (failure) {
         refreshError = failure;
      } finally {
         isRefreshing = false;
         setGlobalLoader(false);
      }

      const waiting = refreshSubscribers;
      refreshSubscribers = [];
      waiting.forEach((settle) => settle(!refreshError));

      if (refreshError) {
         isSessionEnded = true;
         // With the new billing flow off, the refresh refuses an organization
         // whose subscription has lapsed. Signing in again is what shows the
         // subscription screen, so the login page is told to say so.
         AuthService.logout(isSubscriptionEnded(refreshError) ? LogoutReason.SUBSCRIPTION : undefined);
         return Promise.reject(error);
      }

      return retryWithCurrentToken(originalRequest);
   };
};

// The refresh's refusal names the subscription. The old flow sends no code, so
// it is told by its sentence, which BadRequest puts in `errors`.
const isSubscriptionEnded = (refreshError) => {
   const envelope = refreshError?.response?.data ?? refreshError?.envelope;
   return [envelope?.message, ...(Array.isArray(envelope?.errors) ? envelope.errors : [])].some(
      isSubscriptionEndedMessage
   );
};

export default https;
